import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getStockRequestById,
  getStockRecord,
  getStockNotes,
  getStockRequestTimeline,
  approveStockRequest,
  dispatchStockRequest,
  confirmStockRequest,
} from "../../../redux/StoreManagement/storeManagementActions";
import GoBack from "../../../components/GoBack";
import Header from "../../../components/Header";
import {
  ContentCopy,
  DoneAll,
  Schedule,
  MailOutline,
  Clear,
  RemoveCircleOutline,
  AddCircleOutline,
  LocalShipping,
  AssignmentTurnedIn,
  // Cancel,
  Block,
  Inventory,
} from "@mui/icons-material";
import inventory_icon from "../../../assets/icons/inventory.svg";
import clock_loader_10 from "../../../assets/icons/clock_loader_10.svg";
import deployed_code_history from "../../../assets/icons/deployed_code_history.svg";
import remove_circle_outline from "../../../assets/icons/remove_circle_outline.svg";
import ModalComponent from "../../../components/Modals/modal";
import AddNoteModal from "./RequestNoteModal";
import toast from "react-hot-toast";
import moment from "moment";
import RequestTimelineDrawer from "../../StoreManagement/StockRequests/RequestTimelineDrawer";
import { hasPermission } from "../../../utils/permissionChecker";
import { PRIVILEGES } from "../../../utils/privileges";

const StockRequestDetailsPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  // Redux state
  const {
    currentStockRequest,
    currentStockRequestLoading,
    stockRecordLoading,
    stockRecordTotalQuantity,
    stockRecordData,
    stockNotes,
    stockNotesLoading,
    stockRequestTimeline,
    stockRequestTimelineLoading,
    stockRequestTimelineError,
    addStockLoading,
    confirmStockLoading,
  } = useSelector((state) => state.storeManagement);
  const { user, userInfo } = useSelector((state) => state.auth);

  // Local state

  // const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectDispatchModal, setRejectDispatchModal] = useState(false);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [availableFilter, setAvailableFilter] = useState("all");
  const [openStockDetails, setOpenStockDetails] = useState(null);
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [showTimeline, setShowTimeline] = useState(false);

  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [deliveryMethod, setDeliveryMethod] = useState("");
  const [deliveryDate, setDeliveryDate] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverPhone, setDriverPhone] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [dispatchQuantities, setDispatchQuantities] = useState({});

  const [approvalRejectionReason, setApprovalRejectionReason] = useState("");
  const [approvalRejectionNote, setApprovalRejectionNote] = useState("");
  const [processingRejectionReason, setProcessingRejectionReason] =
    useState("");
  const [processingRejectionNote, setProcessingRejectionNote] = useState("");
  const [rejectDispatchTermsAccepted, setRejectDispatchTermsAccepted] =
    useState(false);

  const [showConfirmReceiptModal, setShowConfirmReceiptModal] = useState(false);
  const [activeReceiptStock, setActiveReceiptStock] = useState(null);
  const [receivedQuantities, setReceivedQuantities] = useState({});
  const [sellingPrices, setSellingPrices] = useState({});
  const [confirmationNote, setConfirmationNote] = useState("");
  const [profit, setProfit] = useState(0);
  const [margin, setMargin] = useState(0);

  const [selectedStoreId, setSelectedStoreId] = useState(null);

  // Example delivery methods
  const deliveryMethods = [
    { value: "INTERNAL", label: "Internal Logistics" },
    { value: "3RD_PARTY", label: "3rd Party Courier" },
    { value: "DIRECT_PICKUP", label: "Direct Pickup" },
  ];

  const rejectionReasons = [
    "Out of Stock",
    "Insufficient Quantity",
    "Damaged Stock",
    "Invalid Request",
    "Restricted Product",
    "Other (please specify)",
  ];

  const processingRejectionReasons = [
    "Insufficient Quantity Available ",
    "Items Damaged / Quality Issue",
    "Logistics Unavailable",
    "Inventory on Hold / Audit in Progress",
    "Restricted Product for this Route ",
    "Other(Add Note)",
  ];

  const handleStoreSelection = (storeId) => {
    if (selectedStoreId && selectedStoreId !== storeId) {
      // Reset all quantities when switching stores
      setSelectedQuantities({});
    }
    setSelectedStoreId(storeId);
  };

  // Add clear selection handler
  const handleClearStoreSelection = () => {
    setSelectedStoreId(null);
    setSelectedQuantities({});
  };

  const handleIncrement = (stockId, storeId) => {
    // Set selected store if not already set
    if (!selectedStoreId) {
      setSelectedStoreId(storeId);
    }

    // Only allow increment if this is the selected store
    if (selectedStoreId === storeId) {
      setSelectedQuantities((prev) => ({
        ...prev,
        [stockId]: (prev[stockId] || 0) + 1,
      }));
    }
  };

  const handleDecrement = (stockId, storeId) => {
    // Only allow decrement if this is the selected store
    if (selectedStoreId === storeId) {
      setSelectedQuantities((prev) => ({
        ...prev,
        [stockId]: Math.max((prev[stockId] || 0) - 1, 0),
      }));

      // Clear selected store if no quantities remain
      const remainingQuantities = Object.values({
        ...selectedQuantities,
        [stockId]: Math.max((selectedQuantities[stockId] || 0) - 1, 0),
      }).some((qty) => qty > 0);

      if (!remainingQuantities) {
        setSelectedStoreId(null);
      }
    }
  };

  const handleToggleStockDetails = (locationId) => {
    setOpenStockDetails((prev) => (prev === locationId ? null : locationId));
  };

  // Helper to get total selected for this stock group
  const getTotalSelectedForStock = (stock) => {
    if (!Array.isArray(stock.stock_records)) return 0;
    return stock.stock_records.reduce(
      (sum, record) => sum + (selectedQuantities[record.stock_id] || 0),
      0
    );
  };

  // Add to first available record
  const handleStockGroupIncrement = (stock) => {
    handleStoreSelection(stock.location.id);

    if (!Array.isArray(stock.stock_records)) return;
    for (let i = 0; i < stock.stock_records.length; i++) {
      const record = stock.stock_records[i];
      const current = selectedQuantities[record.stock_id] || 0;
      if (current < record.current_quantity) {
        setSelectedQuantities((prev) => ({
          ...prev,
          [record.stock_id]: current + 1,
        }));
        break;
      }
    }
  };

  const handleStockGroupDecrement = (stock) => {
    if (selectedStoreId !== stock.location.id) return;

    if (!Array.isArray(stock.stock_records)) return;
    for (let i = stock.stock_records.length - 1; i >= 0; i--) {
      const record = stock.stock_records[i];
      const current = selectedQuantities[record.stock_id] || 0;
      if (current > 0) {
        setSelectedQuantities((prev) => ({
          ...prev,
          [record.stock_id]: current - 1,
        }));
        break;
      }
    }
  };

  // Handlers for increment and decrement
  const handleDispatchIncrement = (stockId, maxQty) => {
    setDispatchQuantities((prev) => ({
      ...prev,
      [stockId]: Math.min((prev[stockId] || 0) + 1, maxQty),
    }));
  };

  const handleDispatchDecrement = (stockId) => {
    setDispatchQuantities((prev) => ({
      ...prev,
      [stockId]: Math.max((prev[stockId] || 0) - 1, 0),
    }));
  };

  const getStatusIcon = (status) => {
    const iconProps = { style: { fontSize: 14 } };
    switch (status?.toUpperCase()) {
      case "DISPATCHED":
        return <LocalShipping {...iconProps} />;
      case "DELIVERED":
      case "COMPLETED":
        return <AssignmentTurnedIn {...iconProps} />;
      case "CANCELLED":
        return <Block {...iconProps} />;
      case "PROCESSING":
        return <Inventory {...iconProps} />;
      case "FULFILLED":
        return <img src={inventory_icon} alt="Fulfilled" {...iconProps} />;
      case "PART_APPROVED":
        return <RemoveCircleOutline {...iconProps} />;

      default:
        return null;
    }
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      DISPATCHED: "border-[#38AFE0] text-[#0066AF] bg-[#E7FBFE]",
      DELIVERED: "border-[#2B8C34] text-[#2B8C34] ",
      PROCESSING: "border-[#D4A407] text-[#D4A407] ",
      COMPLETED: "border-[#2B8C34] text-[#2B8C34] ",
      CANCELLED: "border-[#E75E63] bg-[#FEF0EC] text-[#B92020] ",
      PENDING: "border-[#D4A407] text-[#D4A407] ",
      STOCK_REQUEST: "border-[#E29700] text-[#E29700] bg-[#FFFFFF] ",
      FULFILLED: "border-[#72B278] text-[#2B8C34] bg-[#E1F7E3]",
      PART_APPROVED: "border-[#C2410C] text-[#C2410C] bg-[#FFFFFF]",
    };
    return styles[status] || "border-[#5F6D60] text-[#5F6D60] bg-[#F5F5F5]";
  };

  const totalRequested = currentStockRequest?.quantity || 0;
  const totalSelected = Array.isArray(stockRecordData)
    ? stockRecordData.reduce(
        (sum, stock) =>
          sum +
          (Array.isArray(stock.stock_records)
            ? stock.stock_records.reduce(
                (subSum, record) =>
                  subSum + (selectedQuantities[record.stock_id] || 0),
                0
              )
            : 0),
        0
      )
    : 0;

  const unitsLeft = Math.max(totalRequested - totalSelected, 0);

  const selectedSources = useMemo(() => {
    if (!Array.isArray(stockRecordData) || !selectedStoreId) return [];

    // Only return the selected store
    const selectedStore = stockRecordData.find(
      (stock) => stock.location.id === selectedStoreId
    );

    if (!selectedStore) return [];

    const selectedRecords = Array.isArray(selectedStore.stock_records)
      ? selectedStore.stock_records.filter(
          (record) => (selectedQuantities[record.stock_id] || 0) > 0
        )
      : [];

    if (selectedRecords.length === 0) return [];

    return [
      {
        location: selectedStore.location?.name,
        location_id: selectedStore.location?.id || "",
        total: selectedRecords.reduce(
          (sum, record) => sum + (selectedQuantities[record.stock_id] || 0),
          0
        ),
        records: selectedRecords.map((record) => ({
          stock_id: record.stock_id,
          quantity: selectedQuantities[record.stock_id] || 0,
        })),
      },
    ];
  }, [stockRecordData, selectedQuantities, selectedStoreId]);

  // Initialize dispatch quantities when selectedSources change
  useEffect(() => {
    // Defensive check for currentStockRequest and fulfillments
    if (
      currentStockRequest &&
      Array.isArray(currentStockRequest.fulfillments) &&
      currentStockRequest.fulfillments.length > 0 &&
      Array.isArray(currentStockRequest.fulfillments[0].fulfillment_details)
    ) {
      const initial = {};
      currentStockRequest.fulfillments[0].fulfillment_details.forEach(
        (detail) => {
          detail.stock_records.forEach((rec) => {
            initial[rec.stock_record_id] = rec.quantity;
          });
        }
      );
      setDispatchQuantities(initial);
    } else if (selectedSources && selectedSources.length > 0) {
      // Fallback to selectedSources if no fulfillment_details
      const initial = {};
      selectedSources.forEach((source) => {
        source.records.forEach((rec) => {
          initial[rec.stock_id] = rec.quantity;
        });
      });
      setDispatchQuantities(initial);
    }
  }, [currentStockRequest, selectedSources]);

  const getDispatchedStockRecords = useCallback(() => {
    if (
      currentStockRequest &&
      Array.isArray(currentStockRequest.fulfillments) &&
      currentStockRequest.fulfillments.length > 0 &&
      Array.isArray(currentStockRequest.fulfillments[0].fulfillment_details)
    ) {
      // Flatten all stock_records from all fulfillment_details
      return currentStockRequest.fulfillments[0].fulfillment_details.flatMap(
        (detail) =>
          detail.stock_records.map((rec) => ({
            stock_id: rec.stock_record_id,
            quantity: rec.quantity,
          }))
      );
    }
    return [];
  }, [currentStockRequest]);

  // Example: Open modal for first dispatched stock
  const handleOpenConfirmReceipt = () => {
    const dispatchedRecords = getDispatchedStockRecords();
    if (dispatchedRecords.length > 0) {
      setActiveReceiptStock(dispatchedRecords[0].stock_id);
      setShowConfirmReceiptModal(true);
    } else {
      toast.info("No dispatched stock records available for confirmation.");
    }
  };

  // Calculate total units confirmed and total stock value
  const totalUnitsConfirmed = Object.values(receivedQuantities).reduce(
    (sum, qty) => sum + (parseInt(qty) || 0),
    0
  );
  const totalStockValue = Object.entries(receivedQuantities).reduce(
    (sum, [stockId, qty]) => {
      const price = parseFloat(sellingPrices[stockId]) || 0;
      return sum + (parseInt(qty) || 0) * price;
    },
    0
  );

  // Example previous selling price lookup
  const getPrevSellingPrice = (stockId) => {
    // Replace with actual lookup if available
    return 3000;
  };

  const calculateProfit = (stockId, newPrice) => {
    if (stockId && newPrice !== undefined && newPrice !== "") {
      const prevPrice = getPrevSellingPrice(stockId);
      const numericNewPrice = parseFloat(newPrice) || 0;
      const calculatedProfit = numericNewPrice - prevPrice;
      setProfit(calculatedProfit);
      if (prevPrice > 0) {
        const marginValue = (calculatedProfit / prevPrice) * 100;
        setMargin(marginValue.toFixed(2));
      } else {
        setMargin(0);
      }
    } else {
      setProfit(0);
      setMargin(0);
    }
  };

  const allDispatchedRecords = useMemo(
    () => getDispatchedStockRecords(),
    [getDispatchedStockRecords]
  );

  // Get current stock index
  const getCurrentStockIndex = useMemo(() => {
    return allDispatchedRecords.findIndex(
      (rec) => rec.stock_id === activeReceiptStock
    );
  }, [allDispatchedRecords, activeReceiptStock]);

  // Check if stock is complete
  const isStockComplete = (stockId) => {
    const hasReceivedQuantity =
      receivedQuantities[stockId] && parseInt(receivedQuantities[stockId]) > 0;
    const hasSellingPrice =
      sellingPrices[stockId] && parseFloat(sellingPrices[stockId]) > 0;
    return hasReceivedQuantity && hasSellingPrice;
  };

  // Check if current stock has required fields filled
  const isCurrentStockComplete = useMemo(() => {
    if (!activeReceiptStock) return false;
    const hasReceivedQuantity =
      receivedQuantities[activeReceiptStock] &&
      parseInt(receivedQuantities[activeReceiptStock]) > 0;
    const hasSellingPrice =
      sellingPrices[activeReceiptStock] &&
      parseFloat(sellingPrices[activeReceiptStock]) > 0;
    return hasReceivedQuantity && hasSellingPrice;
  }, [activeReceiptStock, receivedQuantities, sellingPrices]);
  // Check if this is the last stock
  const isLastStock = useMemo(() => {
    return getCurrentStockIndex === allDispatchedRecords.length - 1;
  }, [getCurrentStockIndex, allDispatchedRecords]);

  // Handle next stock navigation
  const handleNextStock = () => {
    const nextIndex = getCurrentStockIndex + 1;
    if (nextIndex < allDispatchedRecords.length) {
      setActiveReceiptStock(allDispatchedRecords[nextIndex].stock_id);
    }
  };

  // Handle confirm receipt API call
  // ...existing code...

  const handleConfirmReceipt = async () => {
    // Build stock_confirmations array from receivedQuantities
    const stock_confirmations = Object.entries(receivedQuantities)
      .filter(([stock_id, qty]) => qty && parseInt(qty) > 0)
      .map(([stock_id, qty]) => ({
        stock_id,
        quantity_confirmed: parseInt(qty),
      }));

    // Build custom_prices object from sellingPrices (add cost_price/market_price if available)
    const custom_prices = {};
    Object.entries(sellingPrices).forEach(([stock_id, price]) => {
      if (price && parseFloat(price) > 0) {
        custom_prices[stock_id] = {
          selling_price: parseFloat(price),
          // Add cost_price and market_price if you have them in state
          // cost_price: ...,
          // market_price: ...,
        };
      }
    });

    // Build payload
    const payload = {
      stock_confirmations,
      notes: confirmationNote,
    };
    if (Object.keys(custom_prices).length > 0) {
      payload.custom_prices = custom_prices;
    }

    // TODO: Call your confirm receipt API here, e.g.
    // await dispatch(confirmReceiptStockRequest({ requestId: id, payload }));
    try {
      await dispatch(confirmStockRequest({ requestId: id, payload })).unwrap();
      dispatch(getStockRequestById(id));
      dispatch(getStockRequestTimeline({ requestId: id }));
      setShowConfirmReceiptModal(false);
    } catch (error) {
      toast.error("Receipt confirmation failed. Please try again.");
    }

    setShowConfirmReceiptModal(false);
  };

  // ...existing code...

  // Updated button onClick logic
  const handleDoubleButton = () => {
    if (isLastStock) {
      handleConfirmReceipt();
    } else {
      handleNextStock();
    }
  };

  const StockRecordLoader = () => (
    <div className="animate-pulse w-full">
      <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
      <div className="h-8 bg-gray-200 rounded-lg w-full mb-2"></div>
      <div className="h-8 bg-gray-200 rounded-lg w-full"></div>
    </div>
  );

  const TimelineLoader = () => (
    <div className="animate-pulse w-full px-4 mt-4 mb-4">
      <div className="h-8 bg-gray-200 rounded-lg w-full mb-2"></div>
      <div className="h-6 bg-gray-200 rounded-lg w-3/4 mb-2"></div>
      <div className="h-6 bg-gray-200 rounded-lg w-1/2"></div>
    </div>
  );

  // Button disabled logic
  const isDispatchDisabled =
    !deliveryMethod ||
    !deliveryDate ||
    !driverName ||
    !driverPhone ||
    Object.keys(dispatchQuantities).length === 0 ||
    // Optionally, check that all dispatch quantities are valid numbers
    Object.values(dispatchQuantities).some(
      (qty) => qty === undefined || qty === "" || isNaN(qty)
    );

  // Helper to get quantity adjustments only for changed quantities
  const getQuantityAdjustments = () => {
    // Map from fulfillment_details
    if (
      Array.isArray(currentStockRequest.fulfillments) &&
      currentStockRequest.fulfillments.length > 0 &&
      Array.isArray(currentStockRequest.fulfillments[0].fulfillment_details)
    ) {
      const adjustments = {};
      currentStockRequest.fulfillments[0].fulfillment_details.forEach(
        (detail) => {
          detail.stock_records.forEach((rec) => {
            // Only include if dispatch quantity is different from allocated quantity
            if (
              dispatchQuantities[rec.stock_record_id] !== undefined &&
              dispatchQuantities[rec.stock_record_id] !== rec.quantity
            ) {
              adjustments[rec.stock_record_id] =
                dispatchQuantities[rec.stock_record_id];
            }
          });
        }
      );
      // Only return if there are any adjustments
      return Object.keys(adjustments).length > 0 ? adjustments : undefined;
    }
    return undefined;
  };

  const getExpiryDateStyling = (expiryDate) => {
    if (!expiryDate) return { color: "#5F6D60", borderColor: "#E1E6E1" };

    const today = moment();
    const expiry = moment(expiryDate);
    const daysUntilExpiry = expiry.diff(today, "days");

    if (daysUntilExpiry <= 7) {
      // Red for 1 week or less
      return { color: "#B92020", borderColor: "#B92020" };
    } else if (daysUntilExpiry <= 30) {
      // Amber for 1 month or less
      return { color: "#D4A407", borderColor: "#D4A407" };
    } else {
      // Green for longer than 1 month
      return { color: "#2B8C34", borderColor: "#2B8C34" };
    }
  };
  //
  // Find the request data based on ID
  useEffect(() => {
    if (id) {
      dispatch(getStockRequestById(id));
    }
  }, [dispatch, id]);

  // Get stock notes
  useEffect(() => {
    if (id) {
      dispatch(getStockNotes(id));
      dispatch(getStockRequestTimeline({ requestId: id }));
    }
  }, [dispatch, id]);

  // Get Stock Record - only if user has APPROVE_STOCK permission
  useEffect(() => {
    if (currentStockRequest && hasPermission(user, PRIVILEGES.APPROVE_STOCK)) {
      dispatch(
        getStockRecord({
          productVariantId: currentStockRequest?.original?.product_variant?.id,
          locationId: currentStockRequest?.original?.requesting_location?.id,
          filter: availableFilter,
        })
      );
    }
  }, [dispatch, availableFilter, currentStockRequest, user]);

  useEffect(() => {
    if (Array.isArray(stockRecordData)) {
      const initial = {};
      stockRecordData.forEach((stock) => {
        if (Array.isArray(stock.stock_records)) {
          stock.stock_records.forEach((record) => {
            initial[record.stock_id] = 0;
          });
        }
      });
      setSelectedQuantities(initial);
    }
  }, [stockRecordData]);

  // Fetch notes for saving
  const fetchNotes = () => {
    dispatch(getStockNotes(id));
  };

  // Handle copy to clipboard
  const handleCopyNumber = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(`${text} copied successfully`);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear().toString().slice(-2);
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return {
      date: `${day} ${month} ${year}`,
      time: time,
    };
  };

  // Approval Status Icon
  const getApprovalStatusIcon = (status) => {
    switch (status) {
      case "PENDING":
      case "PENDING_APPROVAL":
        return (
          <Schedule
            className="text-[#D4A407]"
            style={{ width: "14px", height: "14px" }}
          />
        );
      case "PROCESSING":
        return <img src={deployed_code_history} alt="Processing" />;
      case "DISPATCHED":
        return (
          <LocalShipping
            className="text-[#0066AF]"
            style={{ width: "14px", height: "14px" }}
          />
        );
      case "FULFILLED":
        return (
          <AssignmentTurnedIn
            className="text-[#2B8C34]"
            style={{ width: "14px", height: "14px" }}
          />
        );
      case "PART_FULFILLED":
        return (
          <img
            src={clock_loader_10}
            alt="Part Fulfilled Icon"
            style={{ width: "14px", height: "14px" }}
          />
        );
      case "REJECTED":
        return (
          <Clear
            className="text-[#B92020]"
            style={{ width: "14px", height: "14px" }}
          />
        );
      case "CANCELLED":
        return (
          <Block
            className="text-[#B92020]"
            style={{ width: "14px", height: "14px" }}
          />
        );
      default:
        return (
          <Schedule
            className="text-[#96A397]"
            style={{ width: "14px", height: "14px" }}
          />
        );
    }
  };

  // Approval Status Badge
  const getApprovalStatusBadge = (status) => {
    switch (status) {
      case "PENDING":
      case "PENDING_APPROVAL":
        return "bg-[#FFFFFF] text-[#D4A407] border border-[#D4A407]";
      case "PROCESSING":
        return "bg-[#FFFCC5] text-[#D4A407] border border-[#FFC508]";
      case "DISPATCHED":
        return "bg-[#E7FBFE] text-[#0066AF] border border-[#38AFE0]";
      case "FULFILLED":
        return "bg-[#E1F7E3] text-[#2B8C34] border border-[#72B278]";
      case "PART_FULFILLED":
        return "bg-[#FFEDD5] text-[#C2410C] border border-[#F97316]";
      case "REJECTED":
        return "bg-[#FEF0EC] text-[#B92020] border border-[#E75E63]";
      case "CANCELLED":
        return "bg-[#FEF0EC] text-[#B92020] border border-[#E75E63]";
      default:
        return "bg-[#ECEEEC] text-[#96A397] border border-[#96A397]";
    }
  };

  const formatFulfillmentStatus = (status) => {
    switch (status) {
      case "--":
        return "No Item";
      case "PROCESSING":
        return "Processing";
      case "DISPATCHED":
        return "Dispatched";
      case "CONFIRMED":
        return "Confirmed";
      case "CANCELLED":
        return "Cancelled";
      case "PENDING":
        return "Pending";
      case "PART_FULFILLED":
        return "Part Fulfilled";
      case "PART_DELIVERY":
        return "Part Delivery";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    }
  };

  const handleConfirmApproval = async () => {
    // Build payload for approveStockRequest API
    const payload = {
      action: "approve",
      data: selectedSources.map((source) => ({
        location_id: source.location_id,
        stock_items: source.records.map((rec) => ({
          stock_id: rec.stock_id,
          quantity: rec.quantity,
        })),
      })),
    };
    try {
      await dispatch(
        approveStockRequest({
          requestId: id,
          payload,
        })
      ).unwrap();
      // After successful approval, fetch updated request details
      dispatch(getStockRequestById(id));
      setShowConfirmModal(false);
      dispatch(getStockRequestTimeline({ requestId: id }));
    } catch (error) {
      toast.error("Approval failed. Please try again.");
    }
  };

  const handleRejectApproval = async () => {
    const payload = {
      action: "reject",
      rejection_reason: approvalRejectionReason,
    };
    // Add rejection note if "Other (please specify)" is selected
    if (approvalRejectionReason === "Other (please specify)") {
      payload.note = approvalRejectionNote;
    }
    try {
      await dispatch(
        approveStockRequest({
          requestId: id,
          payload,
        })
      ).unwrap();
      // After successful rejection, fetch updated request details
      dispatch(getStockRequestById(id));
      setShowRejectModal(false);
      dispatch(getStockRequestTimeline({ requestId: id }));
      // toast.success("Stock request rejected successfully!");
    } catch (error) {
      toast.error("Rejection failed. Please try again.");
    }
  };

  // Confirm Dispatch handler
  const handleConfirmDispatch = async () => {
    const payload = {
      action: "dispatch",
      delivery_method: deliveryMethod,
      estimated_delivery_date: deliveryDate,
      driver_courier_name: driverName,
      phone_number: driverPhone,
      delivery_note: deliveryNote,
    };

    const quantityAdjustments = getQuantityAdjustments();
    if (quantityAdjustments) {
      payload.quantity_adjustments = quantityAdjustments;
    }

    try {
      await dispatch(dispatchStockRequest({ requestId: id, payload })).unwrap();
      dispatch(getStockRequestById(id));
      setShowDispatchModal(false);
      dispatch(getStockRequestTimeline({ requestId: id }));
    } catch (error) {
      toast.error("Dispatch failed. Please try again.");
    }
  };

  const handleRejectDispatch = async () => {
    const payload = {
      action: "cancel",
      cancellation_reason: processingRejectionReason,
    };

    // Add cancellation note if "Other(Add Note)" is selected
    if (processingRejectionReason === "Other(Add Note)") {
      payload.note = processingRejectionNote;
    }

    try {
      // Using the same dispatchStockRequest thunk since it handles the same endpoint
      await dispatch(dispatchStockRequest({ requestId: id, payload })).unwrap();

      // Update the UI after successful cancellation
      await Promise.all([
        dispatch(getStockRequestById(id)),
        dispatch(getStockRequestTimeline({ requestId: id })),
      ]);

      setRejectDispatchModal(false);
      // toast.success("Stock request cancelled successfully!");
    } catch (error) {
      toast.error("Failed to cancel stock request. Please try again.");
    }
  };

  if (currentStockRequestLoading) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <p className="font-medium text-sm leading-5">Loading...</p>
      </div>
    );
  }

  if (!currentStockRequest) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <p className="font-medium text-sm leading-5">Request not found</p>
      </div>
    );
  }

  return (
    <div>
      <GoBack />

      <AddNoteModal
        show={showAddNoteModal}
        setShow={setShowAddNoteModal}
        notes={stockNotes}
        fetchNotes={fetchNotes}
      />

      <div className="mt-4">
        <Header text="Stock Request Details" />
      </div>

      <div className="mt-4 flex gap-4">
        {/* Left Column - Requested By */}
        <div className="w-[25%]">
          <div>
            <div className="border border-[#E1E6E1] rounded-t-[8px] py-3 px-4 bg-white flex justify-between items-center">
              <p className="text-[#4B564D] font-semibold text-sm">
                Requested By
              </p>
            </div>
            <div className="border-b border-l border-r border-[#E1E6E1] rounded-b-lg p-4 bg-white flex flex-col h-fit">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-[8px] text-base">
                  {/* Requester Initials */}
                  <div className="w-8 h-8 bg-[#2B8C34] rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {currentStockRequest.requested_by?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-[#344335] font-semibold leading-[18px] text-sm">
                      {currentStockRequest.requested_by}
                    </p>
                    <div className="flex items-center gap-[4px] mt-[4px]">
                      <p className="text-[#5F6D60] font-normal text-xs">
                        +2348123456789
                      </p>
                      <ContentCopy
                        onClick={() => handleCopyNumber("+2348123456789")}
                        className="text-[#5F6D60] cursor-pointer"
                        style={{ fontSize: "14px" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="border-[#2B8C34] border bg-white w-[32px] h-[32px] rounded-full flex justify-center items-center">
                  <MailOutline
                    className="text-[#2B8C34]"
                    style={{ width: "16px", height: "16px" }}
                  />
                </div>
              </div>

              <div className="mt-4">
                <p className="text-[#96A397] font-semibold text-xs pb-1">
                  Store
                </p>
                <div className="flex gap-1 items-center bg-[#F6F7F6] px-[6px] border border-[#E1E6E1] py-[2px] w-fit rounded-[4px]">
                  <h6 className=" font-semibold text-xs text-[#5F6D60]">
                    {currentStockRequest.original.requesting_location.name}
                  </h6>
                </div>
              </div>

              <div className="mt-3">
                <p className="text-[#96A397] font-semibold text-xs ">
                  Date Requested
                </p>
                <p className="text-[#5F6D60] font-semibold text-xs mt-1">
                  {currentStockRequest.date_requested}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Middle Column - Request Info & Available Stock */}
        <div className="w-[50%]">
          {/* Request Info Section */}
          <div>
            <div className="border border-[#E1E6E1] rounded-t-[8px] py-3 px-4 bg-white flex justify-between items-center">
              <div className="flex gap-2 items-center">
                <p className="text-[#4B564D] font-semibold text-sm">
                  Request Info
                </p>
                {/* Request ID with Copy */}
                <div className="flex cursor-pointer gap-1 items-center border border-[#C4CBC5] text-[#5F6D60] text-xs px-1 py-1 rounded-[4px]">
                  <p className="text-[#5F6D60] font-semibold text-xs ml-1">
                    {currentStockRequest.id}
                  </p>
                  <ContentCopy
                    onClick={() => handleCopyNumber(currentStockRequest.id)}
                    className="text-[#96A397]"
                    style={{ fontSize: "16px" }}
                  />
                </div>
              </div>

              {/* Approval Status */}
              <div
                className={`flex gap-1 px-1 py-1 rounded-[4px] items-center ${
                  currentStockRequest.approval_status === "PENDING" ||
                  currentStockRequest.approval_status === "REJECTED"
                    ? getApprovalStatusBadge(
                        currentStockRequest.approval_status
                      )
                    : getApprovalStatusBadge(
                        currentStockRequest.fulfillment_status
                      )
                }`}
              >
                {currentStockRequest.approval_status === "PENDING" ||
                currentStockRequest.approval_status === "REJECTED"
                  ? getApprovalStatusIcon(currentStockRequest.approval_status)
                  : getApprovalStatusIcon(
                      currentStockRequest.fulfillment_status
                    )}
                <p className="font-semibold text-xs">
                  {formatFulfillmentStatus(
                    currentStockRequest.approval_status === "PENDING" ||
                      currentStockRequest.approval_status === "REJECTED"
                      ? currentStockRequest.approval_status
                      : currentStockRequest.fulfillment_status
                  )}
                </p>
              </div>
            </div>

            <div className="border-r border-l border-[#E1E6E1] bg-white flex flex-col gap-4 h-fit">
              <div className="px-4 py-4">
                <div className="border border-[#E1E6E1] rounded-lg bg-white overflow-hidden">
                  <div className="flex border-b border-[#E1E6E1]">
                    <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center  min-w-[120px]">
                      <p className="text-[#5F6D60] text-xs font-semibold">
                        Product requested:
                      </p>
                    </div>
                    <div className="flex-1 py-2 px-3">
                      <div className="flex items-center gap-1">
                        <p className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                          {currentStockRequest.product_name?.replace(
                            /\s+/g,
                            "-"
                          ) || "N/A"}
                        </p>
                        <span className="font-semibold text-lg">/</span>
                        <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                          {currentStockRequest.variant_data?.type || "N/A"}
                        </p>
                        <span className="font-semibold text-lg">/</span>
                        <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                          {currentStockRequest.variant_data?.value_unit ||
                            "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex">
                    <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center  min-w-[120px]">
                      <p className="text-[#5F6D60] text-xs font-semibold">
                        Unit(s) Requested:
                      </p>
                    </div>
                    <div className="flex-1 py-2 px-3">
                      <span className="px-2 py-1 rounded-[4px] bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold text-xs">
                        {currentStockRequest.quantity} units
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Available Stock Section */}
          {hasPermission(user, PRIVILEGES.APPROVE_STOCK) && (
            <>
              {currentStockRequest.approval_status === "PENDING" && (
                <div>
                  <div className="border border-[#E1E6E1] py-3 px-4 bg-white flex justify-between items-center">
                    <p className="text-[#3E473F] font-semibold text-sm">
                      Available Stock
                    </p>
                    <div className="flex gap-1 items-center bg-[#FFFFFF] px-[6px] border border-[#C4CBC5] py-[2px] w-fit rounded-[4px]">
                      <p className="text-[#5F6D60] font-semibold text-xs">
                        {stockRecordTotalQuantity} units in stock
                      </p>
                    </div>
                  </div>

                  <div className="border-x border-b border-[#E1E6E1] rounded-b-lg bg-white">
                    {/* Stock Filters */}
                    <div className="border-b border-[#E1E6E1] mb-4">
                      <div className="flex gap-2 px-4 py-2">
                        <button
                          className={`px-3 py-1 rounded-[4px] text-xs font-semibold border ${
                            availableFilter === "all"
                              ? "bg-[#DBFFDF] border-[#DBFFDF] text-[#2B8C34]"
                              : "bg-[#ECEEEC] border-[#ECEEEC] text-[#5F6D60]"
                          }`}
                          onClick={() => setAvailableFilter("all")}
                        >
                          All (
                          {availableFilter === "all" &&
                          Array.isArray(stockRecordData)
                            ? stockRecordData.length
                            : 0}
                          )
                        </button>
                        <button
                          className={`px-3 py-1 rounded-[4px] text-xs font-semibold border ${
                            availableFilter === "nearest"
                              ? "bg-[#DBFFDF] border-[#DBFFDF] text-[#2B8C34]"
                              : "bg-[#ECEEEC] border-[#ECEEEC] text-[#5F6D60]"
                          }`}
                          onClick={() => setAvailableFilter("nearest")}
                        >
                          Nearest (
                          {availableFilter === "nearest" &&
                          Array.isArray(stockRecordData)
                            ? stockRecordData.length
                            : 0}
                          )
                        </button>
                        <button
                          className={`px-3 py-1 rounded-[4px] text-xs font-semibold border ${
                            availableFilter === "most_available"
                              ? "bg-[#DBFFDF] border-[#DBFFDF] text-[#2B8C34]"
                              : "bg-[#ECEEEC] border-[#ECEEEC] text-[#5F6D60]"
                          }`}
                          onClick={() => setAvailableFilter("most_available")}
                        >
                          Most Stock Available (
                          {availableFilter === "most_available" &&
                          Array.isArray(stockRecordData)
                            ? stockRecordData.length
                            : 0}
                          )
                        </button>
                      </div>
                    </div>

                    <div className="px-4 pb-4">
                      {stockRecordLoading ? (
                        <StockRecordLoader />
                      ) : Array.isArray(stockRecordData) &&
                        stockRecordData.length > 0 ? (
                        stockRecordData.map((stock, idx) => {
                          const isSelectedStore =
                            selectedStoreId === stock.location.id;
                          const isDisabled =
                            selectedStoreId &&
                            selectedStoreId !== stock.location.id;

                          return (
                            <div
                              key={stock.location.id}
                              className={`border border-[#E1E6E1] rounded-lg bg-[#fff] overflow-hidden ${
                                idx !== stockRecordData.length - 1
                                  ? " mb-4"
                                  : ""
                              } ${isDisabled ? "opacity-50" : ""} ${
                                isSelectedStore ? "ring-1 ring-[#2B8C34]" : ""
                              }`}
                            >
                              <div className="flex justify-between items-center bg-[#F6F7F6] border-b border-[#E1E6E1] p-2">
                                <div className="flex items-center gap-2">
                                  <h6
                                    className={`bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] text-[#3E473F] font-semibold text-xs`}
                                  >
                                    {stock.location?.name}
                                  </h6>
                                </div>
                                <span className="text-[#5F6D60] bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] font-semibold text-xs">
                                  {Array.isArray(stock.stock_records)
                                    ? stock.stock_records.reduce(
                                        (sum, record) =>
                                          sum + (record.current_quantity || 0),
                                        0
                                      )
                                    : 0}{" "}
                                  Units
                                </span>
                              </div>

                              <div className="flex justify-between bg-[#FFFFFF] items-center p-2">
                                <span className="text-[#5F6D60] font-medium text-xs">
                                  Select Unit(s)
                                </span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() =>
                                      handleStockGroupDecrement(stock)
                                    }
                                    disabled={
                                      isDisabled ||
                                      getTotalSelectedForStock(stock) === 0
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      cursor:
                                        isDisabled ||
                                        getTotalSelectedForStock(stock) === 0
                                          ? "not-allowed"
                                          : "pointer",
                                    }}
                                  >
                                    <RemoveCircleOutline
                                      className={`text-[#5F6D60]${
                                        isDisabled ||
                                        getTotalSelectedForStock(stock) === 0
                                          ? " opacity-50"
                                          : ""
                                      }`}
                                      style={{ fontSize: 20 }}
                                    />
                                  </button>
                                  <span className="text-[#3E473F] font-semibold text-sm text-center min-w-[16px]">
                                    {getTotalSelectedForStock(stock)}
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleStockGroupIncrement(stock)
                                    }
                                    disabled={
                                      isDisabled ||
                                      getTotalSelectedForStock(stock) >=
                                        stock.stock_records.reduce(
                                          (sum, record) =>
                                            sum +
                                            (record.current_quantity || 0),
                                          0
                                        )
                                    }
                                    style={{
                                      background: "none",
                                      border: "none",
                                      padding: 0,
                                      cursor:
                                        isDisabled ||
                                        getTotalSelectedForStock(stock) >=
                                          stock.stock_records.reduce(
                                            (sum, record) =>
                                              sum +
                                              (record.current_quantity || 0),
                                            0
                                          )
                                          ? "not-allowed"
                                          : "pointer",
                                    }}
                                  >
                                    <AddCircleOutline
                                      className={`text-[#3E473F]${
                                        isDisabled ||
                                        getTotalSelectedForStock(stock) >=
                                          stock.stock_records.reduce(
                                            (sum, record) =>
                                              sum +
                                              (record.current_quantity || 0),
                                            0
                                          )
                                          ? " opacity-50"
                                          : ""
                                      }`}
                                      style={{ fontSize: 20 }}
                                    />
                                  </button>
                                </div>
                              </div>

                              {/* Rest of your existing stock details code... */}
                              {openStockDetails === stock.location.id && (
                                <>
                                  <div className="bg-[#F6F7F6] p-2 border-t border-b border-[#E1E6E1]">
                                    <span className="text-[#5F6D60] font-semibold text-xs">
                                      Stock ID/Unit/Expiry Date
                                    </span>
                                  </div>
                                  {stock.stock_records.map((record, idx) => (
                                    <div
                                      key={record.stock_id}
                                      className={`bg-[#FFFFFF] ${
                                        idx !== stock.stock_records.length - 1
                                          ? " border-b border-[#E1E6E1]"
                                          : ""
                                      }`}
                                    >
                                      <div className="flex justify-between items-center py-2 px-2 bg-[#FFFFFF]">
                                        <div className="flex items-center gap-1">
                                          <span className="border w-fit py-[2px] px-1 border-[#E1E6E1] rounded-md font-semibold text-[#5F6D60] text-xs">
                                            {record.stock_id}
                                          </span>
                                          <span className="text-[#5F6D60] text-sm">
                                            /
                                          </span>
                                          <span className="border w-fit py-[2px] px-1 border-[#E1E6E1] rounded-md font-semibold text-[#5F6D60] text-xs">
                                            {record.current_quantity} Units
                                          </span>
                                          <span className="text-[#5F6D60] text-sm">
                                            /
                                          </span>
                                          <span
                                            className={`px-1 py-[2px] rounded-[4px] text-xs font-semibold border bg-[#FFFFFF]`}
                                            style={{
                                              color: getExpiryDateStyling(
                                                record?.expiry_date
                                              ).color,
                                              borderColor: getExpiryDateStyling(
                                                record?.expiry_date
                                              ).borderColor,
                                            }}
                                          >
                                            {record.expiry_date}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <button
                                            onClick={() =>
                                              handleDecrement(
                                                record.stock_id,
                                                stock.location.id
                                              )
                                            }
                                            disabled={
                                              isDisabled ||
                                              selectedQuantities[
                                                record.stock_id
                                              ] === 0
                                            }
                                            style={{
                                              background: "none",
                                              border: "none",
                                              padding: 0,
                                              cursor:
                                                isDisabled ||
                                                selectedQuantities[
                                                  record.stock_id
                                                ] === 0
                                                  ? "not-allowed"
                                                  : "pointer",
                                            }}
                                          >
                                            <RemoveCircleOutline
                                              className={`text-[#5F6D60] ${
                                                isDisabled ||
                                                selectedQuantities[
                                                  record.stock_id
                                                ] === 0
                                                  ? "opacity-50"
                                                  : ""
                                              }`}
                                              style={{ fontSize: 20 }}
                                            />
                                          </button>
                                          <span className="text-[#3E473F] font-semibold text-sm text-center min-w-[16px]">
                                            {selectedQuantities[
                                              record.stock_id
                                            ] || 0}
                                          </span>
                                          <button
                                            onClick={() =>
                                              handleIncrement(
                                                record.stock_id,
                                                stock.location.id
                                              )
                                            }
                                            disabled={
                                              isDisabled ||
                                              selectedQuantities[
                                                record.stock_id
                                              ] >= record.current_quantity
                                            }
                                            style={{
                                              background: "none",
                                              border: "none",
                                              padding: 0,
                                              cursor:
                                                isDisabled ||
                                                selectedQuantities[
                                                  record.stock_id
                                                ] >= record.current_quantity
                                                  ? "not-allowed"
                                                  : "pointer",
                                            }}
                                          >
                                            <AddCircleOutline
                                              className={`text-[#3E473F] ${
                                                isDisabled ||
                                                selectedQuantities[
                                                  record.stock_id
                                                ] >= record.current_quantity
                                                  ? "opacity-50"
                                                  : ""
                                              }`}
                                              style={{ fontSize: 20 }}
                                            />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}

                              <div className="border-t border-[#E1E6E1]">
                                <button
                                  className="text-[#2B8C34] px-2 py-2 rounded-lg font-semibold text-xs"
                                  onClick={() =>
                                    handleToggleStockDetails(stock.location.id)
                                  }
                                >
                                  {openStockDetails === stock.location.id
                                    ? "Show Less"
                                    : "Show Stock"}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="flex flex-col items-center justify-center py-12">
                          <p className="text-[#5F6D60] text-sm font-semibold">
                            No stock records found for this request.
                          </p>
                        </div>
                      )}
                    </div>

                    {selectedStoreId && (
                      <div className="px-4 py-2 border-t border-[#E1E6E1] bg-[#F6F7F6]">
                        <button
                          onClick={handleClearStoreSelection}
                          className="text-[#B92020] text-xs font-semibold underline"
                        >
                          Clear Store Selection
                        </button>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {currentStockRequest.approval_status === "PENDING" && (
                      <div className="px-4 py-4 border-t flex justify-between items-center border-[#E1E6E1]">
                        <div className="flex gap-3">
                          <button
                            onClick={() => setShowConfirmModal(true)}
                            disabled={totalSelected === 0}
                            className="bg-[#2B8C34] text-white px-6 py-2 rounded-lg font-semibold text-sm hover:bg-[#24752B] disabled:bg-[#ABB6AC] transition-colors"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => setShowRejectModal(true)}
                            className="border border-[#FEF0EC] bg-[#FEF0EC] text-[#B92020] px-6 py-2 rounded-lg font-semibold text-sm hover:bg-[#FEF0EC] transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                        <div>
                          <p className="text-[#2B8C34] font-semibold text-xs mt-2">
                            {unitsLeft} Unit(s) Left
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Allocated Stock */}
          {currentStockRequest.fulfillment_status === "PROCESSING" && (
            <div className="bg-white border-x border-b border-[#E1E6E1] rounded-b-lg">
              <div className="border-t border-b border-[#E1E6E1] px-4 py-3">
                <p className="text-[#3E473F] font-semibold text-sm">
                  Allocated Stock
                </p>
              </div>
              <div className="px-4 py-3">
                {Array.isArray(currentStockRequest.fulfillments) &&
                currentStockRequest.fulfillments.length > 0 &&
                Array.isArray(
                  currentStockRequest.fulfillments[0].fulfillment_details
                ) &&
                currentStockRequest.fulfillments[0].fulfillment_details.length >
                  0 ? (
                  currentStockRequest.fulfillments[0].fulfillment_details.map(
                    (detail, idx) => (
                      <div
                        key={detail.location?.id || idx}
                        className={`bg-[#fff] border border-[#E1E6E1] rounded-lg overflow-hidden ${
                          idx !==
                          currentStockRequest.fulfillments[0]
                            .fulfillment_details.length -
                            1
                            ? " mb-3"
                            : ""
                        }`}
                      >
                        <div className="flex justify-between items-center bg-[#F6F7F6] border-b border-[#E1E6E1] p-2">
                          <h6 className="bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] text-[#3E473F] font-semibold text-xs">
                            {detail.location?.name}
                          </h6>
                          <span className="text-[#5F6D60] bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] font-semibold text-xs">
                            {detail.stock_records.reduce(
                              (sum, rec) => sum + (rec.quantity || 0),
                              0
                            )}{" "}
                            Units
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 p-2">
                          {detail.stock_records.map((rec, i) => (
                            <React.Fragment key={rec.stock_record_id}>
                              <div className="flex items-center gap-1">
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                  {rec.stock_record_id}
                                </span>
                                <span className="font-semibold text-lg">/</span>
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                  {rec.quantity}{" "}
                                  {rec.quantity === 1 ? "Unit" : "Units"}
                                </span>
                              </div>
                              {i < detail.stock_records.length - 1 && (
                                <div className="h-[20px] w-[1px] bg-[#E1E6E1]"></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#79887B] font-semibold text-xs">
                      No stock allocated yet.
                    </p>
                  </div>
                )}
              </div>
              {currentStockRequest.fulfillments[0].fulfillment_details[0]
                ?.location.id !== userInfo?.store_id && (
                <div className="text-[#96A397] font-semibold border-t border-[#E1E6E1] text-xs py-3 px-4">
                  Awaiting Dispatch...
                </div>
              )}
              {console.log("User Data:", userInfo)}

              {currentStockRequest.fulfillments[0].fulfillment_details[0]
                ?.location.id === userInfo?.store_id && (
                <div className="px-4 py-4 border-t flex justify-between items-center border-[#E1E6E1]">
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDispatchModal(true)}
                      className="bg-[#2B8C34] text-white px-6 py-2 rounded-lg font-semibold text-sm hover:bg-[#24752B] disabled:bg-[#ABB6AC] transition-colors"
                    >
                      Dispatch Stock
                    </button>
                    <button
                      onClick={() => setRejectDispatchModal(true)}
                      className="border border-[#FEF0EC] bg-[#FEF0EC] text-[#B92020] px-6 py-2 rounded-lg font-semibold text-sm hover:bg-[#FEF0EC] transition-colors"
                    >
                      Reject Processing
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stock Dispatched Section */}
          {currentStockRequest.fulfillment_status === "DISPATCHED" && (
            <div className="bg-white border-x border-b border-[#E1E6E1] rounded-b-lg">
              <div className="border-t border-b border-[#E1E6E1] px-4 py-3">
                <p className="text-[#3E473F] font-semibold text-sm">
                  Stock Dispatched
                </p>
              </div>
              <div className="px-4 py-3">
                {Array.isArray(currentStockRequest.fulfillments) &&
                currentStockRequest.fulfillments.length > 0 &&
                Array.isArray(
                  currentStockRequest.fulfillments[0].fulfillment_details
                ) &&
                currentStockRequest.fulfillments[0].fulfillment_details.length >
                  0 ? (
                  currentStockRequest.fulfillments[0].fulfillment_details.map(
                    (detail, idx) => (
                      <div
                        key={detail.location?.id || idx}
                        className={`bg-[#fff] border border-[#E1E6E1] rounded-lg overflow-hidden ${
                          idx !==
                          currentStockRequest.fulfillments[0]
                            .fulfillment_details.length -
                            1
                            ? " mb-3"
                            : ""
                        }`}
                      >
                        <div className="flex justify-between items-center bg-[#F6F7F6] border-b border-[#E1E6E1] p-2">
                          <h6 className="bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] text-[#3E473F] font-semibold text-xs">
                            {detail.location?.name}
                          </h6>
                          <span className="text-[#5F6D60] bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] font-semibold text-xs">
                            {detail.stock_records.reduce(
                              (sum, rec) => sum + (rec.quantity || 0),
                              0
                            )}{" "}
                            Units
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 p-2">
                          {detail.stock_records.map((rec, i) => (
                            <React.Fragment key={rec.stock_record_id}>
                              <div className="flex items-center gap-1">
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                  {rec.stock_record_id}
                                </span>
                                <span className="font-semibold text-lg">/</span>
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                  {rec.quantity}{" "}
                                  {rec.quantity === 1 ? "Unit" : "Units"}
                                </span>
                              </div>
                              {i < detail.stock_records.length - 1 && (
                                <div className="h-[20px] w-[1px] bg-[#E1E6E1]"></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#79887B] font-semibold text-xs">
                      No stock dispatched yet.
                    </p>
                  </div>
                )}
              </div>
              {currentStockRequest?.original?.requested_by?.id !==
                userInfo?.id && (
                <div className="text-[#96A397] font-semibold border-t border-[#E1E6E1] text-xs py-3 px-4">
                  Awaiting Confirmation...
                </div>
              )}
              {hasPermission(user, PRIVILEGES.CONFIRM_STOCK) &&
                currentStockRequest?.original?.requested_by?.id ===
                  userInfo?.id && (
                  <div className="px-4 py-4 border-t flex justify-between items-center border-[#E1E6E1]">
                    <div className="flex gap-3">
                      <button
                        onClick={handleOpenConfirmReceipt}
                        className="bg-[#2B8C34] text-white px-6 py-2 rounded-lg font-semibold text-sm hover:bg-[#24752B] disabled:bg-[#ABB6AC] transition-colors"
                      >
                        Confirm Stock
                      </button>
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Stock Confirmed section */}
          {currentStockRequest.fulfillment_status === "FULFILLED" && (
            <div className="bg-white border-x border-b border-[#E1E6E1] rounded-b-lg">
              <div className="border-t border-b border-[#E1E6E1] px-4 py-3">
                <p className="text-[#3E473F] font-semibold text-sm">
                  Stock Confirmed
                </p>
              </div>
              <div className="px-4 py-3">
                {Array.isArray(currentStockRequest.fulfillments) &&
                currentStockRequest.fulfillments.length > 0 &&
                Array.isArray(
                  currentStockRequest.fulfillments[0].fulfillment_details
                ) &&
                currentStockRequest.fulfillments[0].fulfillment_details.length >
                  0 ? (
                  currentStockRequest.fulfillments[0].fulfillment_details.map(
                    (detail, idx) => (
                      <div
                        key={detail.location?.id || idx}
                        className={`bg-[#fff] border border-[#E1E6E1] rounded-lg overflow-hidden ${
                          idx !==
                          currentStockRequest.fulfillments[0]
                            .fulfillment_details.length -
                            1
                            ? " mb-3"
                            : ""
                        }`}
                      >
                        <div className="flex justify-between items-center bg-[#F6F7F6] border-b border-[#E1E6E1] p-2">
                          <h6 className="bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] text-[#3E473F] font-semibold text-xs">
                            {detail.location?.name}
                          </h6>
                          <span className="text-[#5F6D60] bg-[#FFFFFF] py-[2px] border border-[#E1E6E1] px-1 rounded-[4px] font-semibold text-xs">
                            {detail.stock_records.reduce(
                              (sum, rec) => sum + (rec.quantity || 0),
                              0
                            )}{" "}
                            Units
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 p-2">
                          {detail.stock_records.map((rec, i) => (
                            <React.Fragment key={rec.stock_record_id}>
                              <div className="flex items-center gap-1">
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                  {rec.stock_record_id}
                                </span>
                                <span className="font-semibold text-lg">/</span>
                                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                  {rec.quantity}{" "}
                                  {rec.quantity === 1 ? "Unit" : "Units"}
                                </span>
                              </div>
                              {i < detail.stock_records.length - 1 && (
                                <div className="h-[20px] w-[1px] bg-[#E1E6E1]"></div>
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    )
                  )
                ) : (
                  <div className="text-center py-8">
                    <p className="text-[#79887B] font-semibold text-xs">
                      No stock confirmed yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Timeline Section - Dynamic Mapping */}
          <div className="bg-white border border-[#E1E6E1] rounded-lg mt-5">
            <div className="border-b border-[#E1E6E1] px-4 py-3 flex justify-between items-center">
              <p className="text-[#3E473F] font-semibold text-sm">
                Recent Timeline
              </p>
              <button
                onClick={() => setShowTimeline(!showTimeline)}
                className="text-[#2B8C34] text-xs font-semibold underline"
              >
                View Full Timeline
              </button>
            </div>
            <div>
              {stockRequestTimelineLoading ? (
                <>
                  <TimelineLoader />
                  <TimelineLoader />
                  <TimelineLoader />
                </>
              ) : stockRequestTimelineError ? (
                <div className="px-4 py-6 text-[#B92020] text-sm">
                  {stockRequestTimelineError}
                </div>
              ) : stockRequestTimeline.length > 0 ? (
                stockRequestTimeline.slice(0, 2).map((event, idx) => {
                  const { date, time } = formatDateTime(event.created);

                  // Map each event type to its design block
                  switch (event.event_type) {
                    case "DISPATCHED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center gap-2 px-4 py-2 border-b border-[#E1E6E1] bg-[#F6F7F6]">
                            <span
                              className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                                "DISPATCHED"
                              )} font-semibold text-xs flex items-center gap-1`}
                            >
                              {getStatusIcon("DISPATCHED")}
                              Dispatched
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#5F6D60] font-semibold text-xs">
                                Stock Dispatched By:
                              </span>
                              <span className="text-[#3E473F] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity Dispatched:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_dispatched}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2 items-center px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="text-[#344335] font-semibold text-xs">
                              Delivery Method:
                            </span>
                            <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                              {event.event_details?.delivery_method}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 items-center px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="text-[#344335] font-semibold text-xs">
                              Driver / Courier Details:
                            </span>
                            <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                              {event.event_details?.driver_courier_name}
                            </span>
                            <span className="text-[#5F6D60] text-sm">/</span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold px-2 py-1 rounded text-xs flex gap-1 items-center">
                              {event.event_details?.phone_number}
                              <ContentCopy
                                onClick={() =>
                                  handleCopyNumber(
                                    event.event_details?.phone_number
                                  )
                                }
                                className="text-[#96A397] cursor-pointer"
                                style={{ fontSize: "16px" }}
                              />
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    case "APPROVED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="bg-[#E1F7E3] text-[#2B8C34] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1">
                              <DoneAll style={{ fontSize: 16 }} />
                              Approved
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Approved By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity Approved:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_allocated}
                            </span>
                          </div>
                          {/* Donor Stores */}
                          {event.event_details?.donor_stores && (
                            <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Donor Store(s):
                              </span>
                              {event.event_details.donor_stores.map(
                                (store, i) => (
                                  <React.Fragment key={i}>
                                    <div className="flex items-center gap-1">
                                      <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#F6F7F6] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                        {store.location_name}
                                      </span>
                                      <span className="font-semibold text-lg">
                                        /
                                      </span>
                                      <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                        {store.quantity} Units
                                      </span>
                                    </div>
                                    {i <
                                      event.event_details.donor_stores.length -
                                        1 && (
                                      <div className="bg-[#E1E6E1] w-[1px] h-[20px]"></div>
                                    )}
                                  </React.Fragment>
                                )
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    case "PARTIALLY_APPROVED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="bg-[#FFFF] text-[#C2410C] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#C2410C]">
                              <img
                                src={remove_circle_outline}
                                alt=""
                                style={{ width: "12px", height: "12px" }}
                              />{" "}
                              Part Approved
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Part Approved By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity Approved:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_allocated}
                            </span>
                          </div>
                          {/* Donor Stores */}
                          {event.event_details?.donor_stores && (
                            <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Donor Store(s):
                              </span>
                              {event.event_details.donor_stores.map(
                                (store, i) => (
                                  <React.Fragment key={i}>
                                    <div className="flex items-center gap-1">
                                      <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#F6F7F6] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                        {store.location_name}
                                      </span>
                                      <span className="font-semibold text-lg">
                                        /
                                      </span>
                                      <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                        {store.quantity} Units
                                      </span>
                                    </div>
                                    {i <
                                      event.event_details.donor_stores.length -
                                        1 && (
                                      <div className="bg-[#E1E6E1] w-[1px] h-[20px]"></div>
                                    )}
                                  </React.Fragment>
                                )
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    case "REJECTED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="text-[#B92020] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#E75E63]">
                              <Clear style={{ fontSize: 16 }} />
                              Rejected
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Rejected By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="text-[#344335] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="px-2 py-1 font-semibold rounded text-xs text-[#344335]">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          {/* Reason for Rejection */}

                          <div className="flex flex-wrap gap-2 items-center px-4 py-2">
                            <span className="text-[#344335] font-semibold text-xs">
                              Reason for Rejection:
                            </span>

                            <span className="text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                              {event.event_details?.rejection_reason || "N/A"}
                            </span>
                          </div>

                          <div className="py-2 px-4 border-b border-[#E1E6E1]">
                            <div className="px-4 py-2 bg-[#F6F7F6] border border-[#E1E6E1] rounded-lg">
                              <p className="text-[#5F6D60] font-semibold text-xs">
                                Rejection Note:
                                <span className="text-[#79887B] font-medium ml-1">
                                  {event.notes}
                                </span>
                              </p>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    case "FULFILLED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="bg-[#E1F7E3] text-[#2B8C34] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#72B278]">
                              <AssignmentTurnedIn style={{ fontSize: 16 }} />
                              Fulfilled
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Stock Confirmed By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity Confirmed:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_confirmed}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    case "PARTIALLY_FULFILLED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span className="bg-[#FFEDD5] text-[#C2410C] px-3 py-1 rounded font-semibold text-xs flex items-center gap-1 border border-[#F97316]">
                              <img
                                alt="Part Fulfilled Icon"
                                src={clock_loader_10}
                                style={{ fontSize: 16 }}
                              />
                              Part Fulfilled
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Stock Confirmed By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity Confirmed:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_confirmed}
                            </span>
                          </div>
                        </React.Fragment>
                      );
                    case "STOCK_REQUEST":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span
                              className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                                "STOCK_REQUEST"
                              )} font-semibold text-xs flex items-center gap-1`}
                            >
                              {getStatusIcon("STOCK_REQUEST")}
                              Stock Request
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Quantity:
                            </span>
                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.quantity_requested}
                            </span>
                          </div>
                          <div className="px-4 py-2 flex border-b border-[#E1E6E1] flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Product Name:
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                                {event.event_details?.product_name}
                              </span>
                              <span className="font-semibold text-lg">/</span>
                              <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                {event.event_details?.variant_data?.type}
                              </span>
                              <span className="font-semibold text-lg">/</span>
                              <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                                {event.event_details?.variant_data?.value}{" "}
                                {event.event_details?.variant_data?.unit}
                              </span>
                            </div>
                          </div>
                          <div className="flex justify-between px-4 py-2 items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Requested By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Store Manager"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    // Add more cases for other event types as needed
                    case "CANCELLED":
                      return (
                        <React.Fragment key={idx}>
                          <div className="flex justify-between items-center bg-[#F6F7F6] gap-2 px-4 py-2 border-b border-[#E1E6E1]">
                            <span
                              className={`px-2 py-1 rounded border ${getStatusBadgeStyle(
                                "CANCELLED"
                              )} font-semibold text-xs flex items-center gap-1`}
                            >
                              {getStatusIcon("CANCELLED")}
                              Cancelled
                            </span>
                            <span className="text-[#5F6D60] font-semibold text-xs">
                              {time} | {date}
                            </span>
                          </div>
                          <div className="flex justify-between px-4 py-2 border-b border-[#E1E6E1] items-center">
                            <div className="flex flex-wrap gap-2 items-center">
                              <span className="text-[#344335] font-semibold text-xs">
                                Cancelled By:
                              </span>
                              <span className="text-[#344335] font-semibold text-xs">
                                {event.user?.full_name}
                              </span>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.user?.role || "Sales Admin"}
                              </span>
                            </div>
                            <div>
                              <span className="bg-[#F6F7F6] text-[#5F6D60] border border-[#E1E6E1] px-2 py-1 font-semibold rounded text-xs">
                                {event.location?.name}
                              </span>
                            </div>
                          </div>
                          <div className="px-4 py-2 border-b border-[#E1E6E1] flex flex-wrap gap-2 items-center">
                            <span className="text-[#344335] font-semibold text-xs">
                              Reason for Cancellation:
                            </span>

                            <span className="bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] px-2 py-1 rounded text-xs">
                              {event.event_details?.cancellation_reason ||
                                "N/A"}
                            </span>
                          </div>
                          {event.notes && (
                            <div className="py-2 px-4 border-b border-[#E1E6E1]">
                              <div className="px-4 py-2 bg-[#F6F7F6] border border-[#E1E6E1] rounded-lg">
                                <p className="text-[#5F6D60] font-semibold text-xs">
                                  Cancellation Note:
                                  <span className="text-[#79887B] font-medium ml-1">
                                    {event.notes}
                                  </span>
                                </p>
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      );

                    default:
                      return null;
                  }
                })
              ) : (
                <div className="px-4 py-6 text-[#96A397] text-sm">
                  No timeline events found.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Notes */}
        <div className="w-[25%]">
          <div>
            <div className="border border-[#E1E6E1] rounded-t-[8px] py-3 px-4 bg-white flex justify-between items-center">
              <p className="text-[#4B564D] font-semibold text-sm">Notes</p>
              <button
                onClick={() => setShowAddNoteModal(true)}
                className="text-[#2B8C34] font-semibold text-xs"
              >
                + Add note
              </button>
            </div>
            <div className="border-b border-x border-[#E1E6E1] rounded-b-lg bg-white p-4 h-fit">
              {stockNotesLoading ? (
                <div className="animate-pulse w-full px-4 mb-4">
                  {/* Loading shimmer */}
                  <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
                  <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
                  <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
                </div>
              ) : (
                <>
                  {stockNotes.length > 0 ? (
                    <div className="space-y-3">
                      {stockNotes.map((note) => (
                        <div
                          key={note.id}
                          className=" border rounded-md mb-2 border-[#E1E6E1]
                         "
                        >
                          <p className="text-[#344335] font-semibold text-xs mb-1 bg-[#F6F7F6] px-2 py-1 border-b border-[#E1E6E1]">
                            {note?.title}
                          </p>
                          <p className="text-[#5F6D60] text-xs mb-2 px-2 py-2 ">
                            {note?.text}
                          </p>
                          <div className="flex justify-between items-center px-2 py-1 border-t border-[#E1E6E1]">
                            <p className="text-[#5F6D60] text-xs">
                              {note?.created_by?.first_name}
                            </p>
                            <p className="text-[#96A397] text-xs">
                              {(() => {
                                const { date, time } = formatDateTime(
                                  note.created
                                );
                                return `${date}, ${time}`;
                              })()}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-[#5F6D60] text-sm">
                        You haven't added any notes yet
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Approval Modal */}
      <ModalComponent
        show={showConfirmModal}
        title="Confirm Approval"
        showModal={() => setShowConfirmModal(false)}
      >
        <div>
          <p className="text-[#5F6D60] font-medium text-sm mt-3">
            You are about to approve and start processing this stock request.
            The requested units will be allocated from the selected store(s).
          </p>

          <div className="border border-[#E1E6E1] rounded-lg bg-white overflow-hidden mt-3">
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center  min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Product requested:
                </p>
              </div>
              <div className="flex-1 py-2 px-3">
                <div className="flex items-center gap-1">
                  <p className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                    {currentStockRequest.product_name?.replace(/\s+/g, "-") ||
                      "N/A"}
                  </p>
                  <span className="font-semibold text-lg">/</span>
                  <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                    {currentStockRequest.variant_data?.type || "N/A"}
                  </p>
                  <span className="font-semibold text-lg">/</span>
                  <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                    {currentStockRequest.variant_data?.value_unit || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center  min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Unit(s) Requested:
                </p>
              </div>
              <div className="flex-1 py-2 px-3">
                <span className="px-2 py-1 rounded-[4px] bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.quantity} units
                </span>
              </div>
            </div>
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center  min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Requested By:
                </p>
              </div>
              <div className="flex-1 py-2 px-3">
                <p className="text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.requested_by}
                </p>
              </div>
            </div>
            <div className="flex">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center  min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">Store:</p>
              </div>
              <div className="flex-1 py-2 px-3">
                <span className="text-[#5F6D60] border border-[#E1E6E1] rounded px-2 py-1 bg-[#F6F7F6] font-semibold text-xs">
                  {currentStockRequest.original?.requesting_location?.name}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-[#F6F7F6] mt-3 border border-[#E1E6E1] rounded-lg p-2">
            <p className="text-[#3E473F] font-semibold text-xs">
              Source Store(s)
            </p>

            {selectedSources.map((source, idx) => (
              <div
                key={idx}
                className={`mt-2 bg-[#FFFFFF] border border-[#E1E6E1] rounded-lg ${
                  idx !== selectedSources.length - 1 ? "mb-2" : ""
                }`}
              >
                <div className="flex justify-between border-b border-[#E1E6E1] py-2 px-3">
                  <div>
                    <span className="text-[#5F6D60] border border-[#E1E6E1] rounded px-2 py-1 bg-[#F6F7F6] font-semibold text-xs">
                      {source.location}
                    </span>
                  </div>
                  <div>
                    <span className="px-2 py-1 rounded-[4px] bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold text-xs">
                      {source.total} units
                    </span>
                  </div>
                </div>

                <div className="py-2 px-3 flex items-center gap-2 flex-wrap">
                  {source.records.map((rec, i) => (
                    <React.Fragment key={i}>
                      <div className="flex items-center gap-1">
                        <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                          {rec.stock_id}
                        </span>
                        <span className="font-semibold text-lg">/</span>
                        <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                          {rec.quantity} {rec.quantity === 1 ? "Unit" : "Units"}
                        </span>
                      </div>
                      {i < source.records.length - 1 && (
                        <div className="h-[20px] w-[1px] bg-[#E1E6E1]"></div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 justify-end mt-4">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="border border-[#C4CBC5] text-[#5F6D60] px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              disabled={addStockLoading}
              className="bg-[#2B8C34] disabled:bg-[#ABB6AC] text-white px-4 py-2 rounded-lg font-semibold text-sm"
              onClick={handleConfirmApproval}
            >
              Confirm Approval
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* Reject Modal */}
      <ModalComponent
        show={showRejectModal}
        showModal={() => setShowRejectModal(false)}
        title={"Reject Approval"}
      >
        <div>
          <p className="text-[#5F6D60] font-medium text-sm mt-3">
            You are about to reject this stock request. Please select a reason
            and add a note if necessary. The requesting store will be notified
            immediately
          </p>
          <div className="mt-4">
            <label className="block text-[#5F6D60] font-semibold text-xs mb-2">
              Reason for Rejection
            </label>
            <select
              value={approvalRejectionReason}
              onChange={(e) => setApprovalRejectionReason(e.target.value)}
              className="w-full border border-[#E1E6E1] rounded-lg px-3 py-2 text-sm text-[#5F6D60] focus:outline-none focus:ring-2 focus:ring-[#2B8C34]"
            >
              <option value="" disabled>
                Select a reason
              </option>
              {rejectionReasons.map((reason, index) => (
                <option key={index} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
          {/* Rejection Note */}
          <div className="mt-4 ">
            <label className="block text-xs font-semibold text-[#5F6D60] rounded-tr-lg rounded-tl-lg border  border-[#E1E6E1]  bg-[#F6F7F6] py-2 px-3">
              Rejection Note
            </label>
            <textarea
              value={approvalRejectionNote}
              onChange={(e) => setApprovalRejectionNote(e.target.value)}
              className="outline-none border-x border-b border-[#E1E6E1] w-full rounded-br-lg rounded-bl-lg px-4 py-3 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5"
              rows={2}
              placeholder="Add note"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end mt-4">
            <button
              onClick={() => setShowRejectModal(false)}
              className="text-[#5C715E] px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              className="disabled:bg-[#96A397] px-4 py-2 bg-[#B92020]  text-white rounded-lg font-semibold text-sm"
              // Add your dispatch logic here
              disabled={!approvalRejectionReason || addStockLoading}
              onClick={handleRejectApproval} // Close modal on click
            >
              Reject Request
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* Dispatch Modal */}
      <ModalComponent
        show={showDispatchModal}
        title="Confirm Dispatch"
        showModal={() => setShowDispatchModal(false)}
        maxWidth="max-w-4xl"
      >
        <div>
          <p className="text-[#5F6D60] font-medium text-sm mt-3">
            You are about to dispatch this stock request. Provide dispatch
            details so the requesting store can track delivery
          </p>
          {/* Product Info */}
          <div className="border border-[#E1E6E1] rounded-lg bg-white overflow-hidden mt-3">
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Product Requested:
                </p>
              </div>
              <div className="flex-1 py-2 px-3 flex items-center gap-1">
                <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                  {currentStockRequest.product_name?.replace(/\s+/g, "-") ||
                    "N/A"}
                </span>
                <span className="font-semibold text-lg">/</span>
                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.variant_data?.type || "N/A"}
                </span>
                <span className="font-semibold text-lg">/</span>
                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.variant_data?.value_unit || "N/A"}
                </span>
              </div>
            </div>
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Unit(s) Requested:
                </p>
              </div>
              <div className="flex-1 py-2 px-3">
                <span className="px-2 py-1 rounded-[4px] bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.quantity} Units
                </span>
              </div>
            </div>
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Requested By:
                </p>
              </div>
              <div className="flex-1 py-2 px-3 flex items-center gap-2">
                <span className="text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.requested_by}
                </span>
                {currentStockRequest.original?.requester_department && (
                  <span className="bg-[#ECEEEC] px-2 py-1 rounded text-xs text-[#5F6D60]">
                    {currentStockRequest.original.requester_department}
                  </span>
                )}
              </div>
            </div>
            <div className="flex">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">Store:</p>
              </div>
              <div className="flex-1 py-2 px-3">
                <span className="text-[#5F6D60] border border-[#E1E6E1] rounded px-2 py-1 bg-[#F6F7F6] font-semibold text-xs">
                  {currentStockRequest.original?.requesting_location?.name}
                </span>
              </div>
            </div>
          </div>
          {/* Allocated Units Table */}
          <div className="border border-[#E1E6E1] rounded-lg bg-white overflow-hidden mt-3">
            <div className="flex border-b border-[#E1E6E1] bg-[#F6F7F6] px-3 py-2 font-semibold text-xs">
              <div className="flex-1 text-[#5F6D60]">
                Stock ID / Allocated Unit
              </div>
              <div className="w-[120px] text-right text-[#5F6D60]">
                Dispatch Qty
              </div>
            </div>
            {Array.isArray(currentStockRequest.fulfillments) &&
            currentStockRequest.fulfillments.length > 0 &&
            Array.isArray(
              currentStockRequest.fulfillments[0].fulfillment_details
            ) &&
            currentStockRequest.fulfillments[0].fulfillment_details.length >
              0 ? (
              currentStockRequest.fulfillments[0].fulfillment_details.map(
                (detail, idx) =>
                  detail.stock_records.map((rec, i) => (
                    <div
                      key={rec.stock_record_id + i}
                      className="flex items-center border-b border-[#E1E6E1] px-3 py-2 last:border-b-0"
                    >
                      <div className="flex-1 flex items-center gap-2">
                        <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                          {rec.stock_record_id}
                        </span>
                        <span className="font-semibold text-lg">/</span>
                        <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                          {rec.quantity} {rec.quantity === 1 ? "Unit" : "Units"}
                        </span>
                      </div>
                      <div className="w-[120px] flex justify-end items-center gap-2">
                        <button
                          onClick={() =>
                            handleDispatchDecrement(rec.stock_record_id)
                          }
                          disabled={
                            dispatchQuantities[rec.stock_record_id] === 0
                          }
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor:
                              dispatchQuantities[rec.stock_record_id] === 0
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          <RemoveCircleOutline
                            className={`text-[#5F6D60]${
                              dispatchQuantities[rec.stock_record_id] === 0
                                ? " opacity-50"
                                : ""
                            }`}
                            style={{ fontSize: 20 }}
                          />
                        </button>
                        <span className="text-[#3E473F] font-semibold text-sm text-center min-w-[16px]">
                          {dispatchQuantities[rec.stock_record_id]
                            ?.toString()
                            .padStart(2, "0")}
                        </span>
                        <button
                          onClick={() =>
                            handleDispatchIncrement(
                              rec.stock_record_id,
                              rec.quantity
                            )
                          }
                          disabled={
                            dispatchQuantities[rec.stock_record_id] >=
                            rec.quantity
                          }
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor:
                              dispatchQuantities[rec.stock_record_id] >=
                              rec.quantity
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          <AddCircleOutline
                            className={`text-[#3E473F]${
                              dispatchQuantities[rec.stock_record_id] >=
                              rec.quantity
                                ? " opacity-50"
                                : ""
                            }`}
                            style={{ fontSize: 20 }}
                          />
                        </button>
                      </div>
                    </div>
                  ))
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="text-[#5F6D60] text-sm font-semibold">
                  No allocated stock records found for dispatch.
                </p>
              </div>
            )}
          </div>
          {/* Delivery Info */}
          <div className="border border-[#E1E6E1] rounded-lg bg-[#F6F7F6] mt-3 p-3">
            <h6 className="text-[#3E473F] text-sm font-semibold">
              Delivery Info
            </h6>
            <div className="flex flex-wrap gap-4 mt-2">
              <div className="flex-1 min-w-[220px] bg-white p-3 border border-[#E1E6E1] rounded-lg">
                <label className="block text-xs font-semibold text-[#344335] mb-2">
                  Delivery Method
                </label>
                <select
                  value={deliveryMethod}
                  onChange={(e) => setDeliveryMethod(e.target.value)}
                  className={`outline-none border border-[#96A397] w-full h-[44px] rounded-lg px-3 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5 bg-white appearance-none`}
                >
                  <option value="">Select a Delivery Method</option>
                  {deliveryMethods.map((method) => (
                    <option key={method.value} value={method.value}>
                      {method.label}
                    </option>
                  ))}
                </select>
                <label className="block text-xs font-semibold text-[#344335] mt-4 mb-2">
                  Estimated Delivery Date
                </label>
                <input
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  className={`outline-none border border-[#96A397] w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5 bg-white`}
                  placeholder="Select Date"
                />
              </div>
              <div className="flex-1 min-w-[220px] bg-white border border-[#E1E6E1] rounded-lg">
                <div className="p-3 border-b border-[#E1E6E1]">
                  <label className="block text-xs font-semibold text-[#344335] mb-2">
                    Driver / Courier Name
                  </label>
                  <input
                    type="text"
                    value={driverName}
                    onChange={(e) => setDriverName(e.target.value)}
                    className={`outline-none border border-[#96A397] w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
                    placeholder="Driver / Courier Name"
                  />
                </div>
                <div className="p-3 border-b border-[#E1E6E1]">
                  <label className="block text-xs font-semibold text-[#344335] mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={driverPhone}
                    onChange={(e) => setDriverPhone(e.target.value)}
                    className={`outline-none border border-[#96A397] w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
                    placeholder="+234"
                  />
                </div>
                <div className="p-3">
                  <label className="block text-xs font-semibold text-[#344335] mb-2">
                    Delivery Note (optional)
                  </label>
                  <textarea
                    value={deliveryNote}
                    onChange={(e) => setDeliveryNote(e.target.value)}
                    className={`outline-none border border-[#96A397] w-full rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
                    rows={2}
                    placeholder="Add note"
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Modal Actions */}
          <div className="flex gap-3 justify-end mt-4">
            <button
              onClick={() => setShowDispatchModal(false)}
              className="text-[#5C715E] px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              disabled={isDispatchDisabled || addStockLoading}
              className="disabled:bg-[#96A397] px-4 py-2 bg-[#2B8C34]  text-white rounded-lg font-semibold text-sm"
              onClick={handleConfirmDispatch}
            >
              Confirm Dispatch
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* Reject Dispatch Modal */}
      <ModalComponent
        show={showRejectDispatchModal}
        showModal={() => setRejectDispatchModal(false)}
        title={"Reject Processing"}
        maxWidth="max-w-3xl"
      >
        <div>
          <p className="text-[#5F6D60] font-medium text-sm mt-3">
            You’re about to reject processing for this approved stock request.
            The Sales Admin will be notified and this allocation will be put on
            hold.
          </p>
          {/* Product Info */}
          <div className="border border-[#E1E6E1] rounded-lg bg-white overflow-hidden mt-3">
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Product Requested:
                </p>
              </div>
              <div className="flex-1 py-2 px-3 flex items-center gap-1">
                <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-[#FFFFFF] text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                  {currentStockRequest.product_name?.replace(/\s+/g, "-") ||
                    "N/A"}
                </span>
                <span className="font-semibold text-lg">/</span>
                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.variant_data?.type || "N/A"}
                </span>
                <span className="font-semibold text-lg">/</span>
                <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-[#FFFFFF] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.variant_data?.value_unit || "N/A"}
                </span>
              </div>
            </div>
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Unit(s) Requested:
                </p>
              </div>
              <div className="flex-1 py-2 px-3">
                <span className="px-2 py-1 rounded-[4px] bg-[#FFFFFF] border border-[#E1E6E1] text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.quantity} Units
                </span>
              </div>
            </div>
            <div className="flex border-b border-[#E1E6E1]">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">
                  Requested By:
                </p>
              </div>
              <div className="flex-1 py-2 px-3 flex items-center gap-2">
                <span className="text-[#5F6D60] font-semibold text-xs">
                  {currentStockRequest.requested_by}
                </span>
                {currentStockRequest.original?.requester_department && (
                  <span className="bg-[#ECEEEC] px-2 py-1 rounded text-xs text-[#5F6D60]">
                    {currentStockRequest.original.requester_department}
                  </span>
                )}
              </div>
            </div>
            <div className="flex">
              <div className="bg-[#F6F7F6] py-2 px-2 border-r border-[#E1E6E1] flex flex-col justify-center min-w-[120px]">
                <p className="text-[#5F6D60] text-xs font-semibold">Store:</p>
              </div>
              <div className="flex-1 py-2 px-3">
                <span className="text-[#5F6D60] border border-[#E1E6E1] rounded px-2 py-1 bg-[#F6F7F6] font-semibold text-xs">
                  {currentStockRequest.original?.requesting_location?.name}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-[#5F6D60] font-semibold text-xs mb-2">
              Reason for Rejection
            </label>
            <select
              value={processingRejectionReason}
              onChange={(e) => {
                setProcessingRejectionReason(e.target.value);
              }}
              className="w-full border border-[#E1E6E1] rounded-lg px-3 py-2 text-sm text-[#5F6D60] focus:outline-none focus:ring-2 focus:ring-[#2B8C34]"
            >
              <option value="" disabled>
                Select a reason
              </option>
              {processingRejectionReasons.map((reason, index) => (
                <option key={index} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
          </div>
          {/* Rejection Note */}
          <div className="mt-3 ">
            <label className="block text-xs font-semibold text-[#5F6D60] rounded-tr-lg rounded-tl-lg border  border-[#E1E6E1]  bg-[#F6F7F6] py-2 px-3">
              Rejection Note
            </label>
            <textarea
              value={processingRejectionNote}
              onChange={(e) => setProcessingRejectionNote(e.target.value)}
              className="outline-none border-x border-b border-[#E1E6E1] w-full rounded-br-lg rounded-bl-lg px-4 py-3 placeholder:text-[#96A397] focus:outline-none focus:shadow-input text-sm font-medium leading-5"
              rows={2}
              placeholder="Note"
            />
          </div>

          {/* Warning */}
          <div className="mt-2 border border-[#B92020] bg-[#FEF0EC] rounded-lg px-3 py-2">
            <p className="text-[#B92020] font-medium text-sm">
              Rejecting will pause this transfer and keep the requesting store
              informed.{" "}
              <span className="text-[#7D0E0E] font-semibold text-sm">
                No inventory will be deducted
              </span>
              . The Sales Admin may reassign the request or adjust the
              allocation.
            </p>
          </div>

          {/* Accept terms */}
          <div className="mt-3 flex items-center gap-2">
            <input
              type="checkbox"
              checked={rejectDispatchTermsAccepted}
              onChange={(e) => setRejectDispatchTermsAccepted(e.target.checked)}
              className="w-4 h-4 text-[#2B8C34] border border-[#E1E6E1] rounded focus:ring-0 focus:outline-none"
            />
            <p className="text-[#5F6D60] font-semibold text-sm">
              I understand a notification will be sent and this request will
              move to Processing Reject
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end mt-4">
            <button
              onClick={() => setRejectDispatchModal(false)}
              className="text-[#5C715E] px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              className="disabled:bg-[#96A397] px-4 py-2 bg-[#B92020]  text-white rounded-lg font-semibold text-sm"
              // Add your dispatch logic here
              disabled={
                !rejectDispatchTermsAccepted ||
                !processingRejectionReason ||
                addStockLoading
              }
              onClick={handleRejectDispatch} // Close modal on click
            >
              Reject Processing
            </button>
          </div>
        </div>
      </ModalComponent>

      {/* Confirm Stock Modal */}
      <ModalComponent
        show={showConfirmReceiptModal}
        title="Confirm Stock Receipt"
        showModal={() => setShowConfirmReceiptModal(false)}
        maxWidth="max-w-5xl"
      >
        <div>
          <p className="text-[#5F6D60] font-medium text-sm mt-3">
            You are about to confirm receipt of this stock. Enter the received
            quantity and new selling price (previous prices are shown). If fewer
            units are received, provide a reason and optionally add a note. The
            receipt date and time will be logged automatically.
          </p>
          <div className="flex gap-3 mt-4 h-[425px]">
            {/* Left: Stock List */}
            <div className="flex-1 bg-[#FFFF] border border-[#E1E6E1] rounded-lg p-0 flex flex-col">
              <div className="px-3 py-2 bg-[#F6F7F6] border-b border-[#E1E6E1]">
                <p className="font-semibold text-sm text-[#5F6D60]">
                  Stock ID/ Dispatched Qty
                </p>
              </div>
              <div className="flex-1">
                {getDispatchedStockRecords().map((rec) => {
                  const confirmed =
                    receivedQuantities[rec.stock_id] &&
                    parseInt(receivedQuantities[rec.stock_id]) > 0;
                  const stockIndex = getDispatchedStockRecords().findIndex(
                    (r) => r.stock_id === rec.stock_id
                  );
                  const isCurrentStock = activeReceiptStock === rec.stock_id;
                  const stockComplete = isStockComplete(rec.stock_id);
                  const getCurrentStockIndex =
                    getDispatchedStockRecords().findIndex(
                      (r) => r.stock_id === activeReceiptStock
                    );
                  const canClick =
                    isCurrentStock ||
                    stockComplete ||
                    stockIndex === getCurrentStockIndex;

                  return (
                    <div
                      key={rec.stock_id}
                      className={`flex items-center justify-between px-4 py-2 border-b border-[#E1E6E1] ${
                        canClick
                          ? "cursor-pointer hover:bg-[#F8F9F8]"
                          : "cursor-none opacity-50"
                      } ${isCurrentStock ? "bg-[#F2FBF3]" : "bg-[#FFFFFF]"}`}
                      onClick={() => {
                        if (canClick) {
                          setActiveReceiptStock(rec.stock_id);
                        }
                      }}
                    >
                      <div className="flex items-center gap-1">
                        <span className="border w-fit py-1 px-2 border-[#E1E6E1] bg-white text-[#5F6D60] rounded-[4px] font-semibold text-xs">
                          {rec.stock_id}
                        </span>
                        <span className="font-semibold text-lg text-[#5F6D60]">
                          /
                        </span>
                        <span className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-[4px] bg-white text-[#5F6D60] font-semibold text-xs">
                          {rec.quantity} {rec.quantity === 1 ? "Unit" : "Units"}
                        </span>
                      </div>
                      {confirmed && (
                        <span className="text-[#2B8C34] font-semibold text-xs">
                          {receivedQuantities[rec.stock_id]} Unit
                          {receivedQuantities[rec.stock_id] > 1 ? "s" : ""}{" "}
                          Confirmed
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between border-t border-[#E1E6E1] bg-white">
                <span className="text-[#5F6D60] py-2 px-3 font-semibold flex items-center text-sm bg-[#F6F7F6]">
                  Total Units Confirmed:
                </span>
                <div className="py-2 px-3 flex items-center">
                  <span
                    className={`bg-[#fff] border rounded py-0.5 px-1 ${
                      totalUnitsConfirmed && isLastStock
                        ? "text-[#2B8C34] border border-[#E1E6E1]"
                        : "text-[#E29700] border border-[#E29700] bg-[#FFFCC5]"
                    } font-semibold text-xs`}
                  >
                    {totalUnitsConfirmed} Unit(s)
                  </span>
                </div>
              </div>
            </div>
            {/* Right: Confirm for selected stock */}
            <div className="flex-[1.2] bg-[#FFFF] border border-[#E1E6E1] rounded-lg flex flex-col">
              {activeReceiptStock && (
                <>
                  <div className="border-b border-[#E1E6E1] bg-[#FFFF] px-4 py-2">
                    <p className="font-semibold text-sm text-[#3E473F]">
                      Confirm Unit Received for Stock{" "}
                      <span className="border border-[#E1E6E1] rounded px-2 py-1 text-[#344335] font-semibold text-xs bg-white">
                        {activeReceiptStock}
                      </span>
                    </p>
                  </div>
                  <div className="py-4 px-4 bg-[#F6F7F6]">
                    <div className="border bg-white border-[#E1E6E1] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]">
                      <label className="text-sm text-[#3E473F] font-semibold">
                        {" "}
                        Unit(s) Received
                      </label>
                    </div>
                    <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
                      <input
                        type="number"
                        min={0}
                        max={
                          getDispatchedStockRecords().find(
                            (r) => r.stock_id === activeReceiptStock
                          )?.quantity || 0
                        }
                        value={receivedQuantities[activeReceiptStock] || ""}
                        onChange={(e) =>
                          setReceivedQuantities((prev) => ({
                            ...prev,
                            [activeReceiptStock]: e.target.value,
                          }))
                        }
                        onWheel={(e) => e.target.blur()} // Remove focus on scroll
                        className="w-full text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                        placeholder="00"
                      />
                    </div>
                  </div>
                  {/*New Selling Price  */}
                  <div className="border-t border-[#E1E6E1] bg-[#FFFF] px-4 py-2">
                    <p className="font-semibold text-sm text-[#3E473F]">
                      New Selling Price
                    </p>
                  </div>
                  <div className="px-4 bg-[#F6F7F6] py-4 border-y border-[#E1] ">
                    <div className="bg-white">
                      <div className="border-t border-x border-[#E1E6E1] bg-white px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] flex justify-between items-center">
                        <p className="text-sm text-[#3E473F] font-semibold">
                          Selling Price
                        </p>
                        <div className="w-fit px-0.5 text-center text-[#5F6D60] font-semibold text-xs border rounded-[4px]">
                          Prev. SP ₦{" "}
                          {getPrevSellingPrice(
                            activeReceiptStock
                          ).toLocaleString()}{" "}
                        </div>
                      </div>
                      <div className="px-4 py-3 border border-[#E1E6E1] bg-white">
                        <div className="flex items-center">
                          <span className="text-[#5F6D60] font-semibold text-xl pr-2">
                            ₦
                          </span>
                          <input
                            type="number"
                            min={0}
                            value={sellingPrices[activeReceiptStock] || ""}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setSellingPrices((prev) => ({
                                ...prev,
                                [activeReceiptStock]: newValue,
                              }));
                              calculateProfit(activeReceiptStock, newValue);
                            }}
                            onWheel={(e) => e.target.blur()} // Remove focus on scroll
                            className="flex-1 text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                            placeholder="0.00"
                          />
                        </div>
                      </div>

                      {/* Profit Margin Details */}
                      <div className="w-full flex border-t-0 border border-[#E1E6E1] rounded-bl-[8px] rounded-br-[8px]">
                        <div
                          className={`${
                            parseFloat(sellingPrices[activeReceiptStock]) > 0
                              ? profit < 0
                                ? "bg-[#B92020]"
                                : "bg-[#2B8C34]"
                              : "bg-white"
                          } flex rounded-bl-[8px] items-center py-2 px-4`}
                          style={{ flex: "1.5" }}
                        >
                          <span
                            className={`${
                              parseFloat(sellingPrices[activeReceiptStock]) > 0
                                ? "text-white"
                                : "text-[#5F6D60]"
                            } font-semibold text-sm`}
                          >
                            {parseFloat(sellingPrices[activeReceiptStock]) >
                              0 && profit < 0
                              ? "Loss"
                              : "Profit"}
                            :{" "}
                            <span
                              className={`${
                                parseFloat(sellingPrices[activeReceiptStock]) >
                                0
                                  ? "text-white"
                                  : "text-[#96A397] "
                              }`}
                            >
                              ₦{" "}
                              {parseFloat(sellingPrices[activeReceiptStock]) > 0
                                ? Math.abs(profit).toLocaleString()
                                : "0.00"}
                            </span>
                          </span>
                          <div
                            className={`h-4 w-px ${
                              parseFloat(sellingPrices[activeReceiptStock]) > 0
                                ? "bg-white"
                                : "bg-[#E1E6E1]"
                            } mx-3 opacity-50`}
                          ></div>
                          <span
                            className={`${
                              parseFloat(sellingPrices[activeReceiptStock]) > 0
                                ? "text-white"
                                : "text-[#5F6D60]"
                            } font-semibold text-sm`}
                          >
                            Margin:{" "}
                            <span
                              className={`${
                                parseFloat(sellingPrices[activeReceiptStock]) >
                                0
                                  ? "text-white"
                                  : "text-[#96A397]"
                              }`}
                            >
                              {parseFloat(sellingPrices[activeReceiptStock]) > 0
                                ? margin
                                : "0"}
                              %
                            </span>
                          </span>
                        </div>
                        <div
                          className={`${
                            parseFloat(sellingPrices[activeReceiptStock]) > 0
                              ? profit < 0
                                ? "bg-[#FEF8D8] text-[#D8A600]"
                                : "bg-[#F2FBF3] text-[#25702C]"
                              : "bg-white text-white"
                          } flex items-center justify-center rounded-br-[8px] text-sm font-semibold py-2 px-4 border-l border-[#E1E6E1]`}
                          style={{ flex: "1" }}
                        >
                          {parseFloat(sellingPrices[activeReceiptStock]) > 0 ? (
                            <span>
                              ₦
                              {Math.abs(
                                parseFloat(sellingPrices[activeReceiptStock]) -
                                  getPrevSellingPrice(activeReceiptStock)
                              ).toLocaleString()}{" "}
                              {parseFloat(sellingPrices[activeReceiptStock]) >
                              getPrevSellingPrice(activeReceiptStock)
                                ? "Higher Than Prev S.P"
                                : "Lower Than Prev S.P"}
                            </span>
                          ) : (
                            <span>&nbsp;</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Total Stock Value */}
                  <div className="flex justify-between border-t border-[#E1E6E1] bg-white mt-2">
                    <span className="text-[#5F6D60] py-2 px-3 font-semibold flex items-center text-sm bg-[#F6F7F6]">
                      Total Stock Value:
                    </span>
                    <div className="py-2 px-3 flex items-center">
                      <span className="bg-[#fff] rounded px-2 py-1 text-[#344335] font-semibold text-xs">
                        ₦{" "}
                        {totalStockValue.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Confirmation Note */}
          <div className="mt-4 ">
            <label className="block text-xs font-semibold text-[#5F6D60] rounded-tr-lg rounded-tl-lg border  border-[#E1E6E1]  bg-[#F6F7F6] py-2 px-3">
              Confirmation Note
            </label>
            <textarea
              value={confirmationNote}
              onChange={(e) => setConfirmationNote(e.target.value)}
              className="outline-none border-x border-b border-[#E1E6E1] w-full rounded-br-lg rounded-bl-lg px-4 py-3 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5"
              rows={2}
              placeholder="Add note"
            />
          </div>
          {/* Modal Actions */}
          <div className="flex gap-3 justify-end mt-4">
            <button
              onClick={() => setShowConfirmReceiptModal(false)}
              className="text-[#5C715E] px-4 py-2 rounded-lg font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleDoubleButton}
              disabled={!isCurrentStockComplete || confirmStockLoading}
              className={`px-4 py-2 rounded-lg disabled:bg-[#ABB6AC] font-semibold text-sm ${
                isCurrentStockComplete
                  ? "bg-[#2B8C34] text-white"
                  : "bg-[#96A397] text-white cursor-not-allowed"
              }`}
            >
              {isLastStock ? "Confirm Receipt" : "Next Stock"}
            </button>
          </div>
        </div>
      </ModalComponent>

      <RequestTimelineDrawer
        show={showTimeline}
        showDrawer={setShowTimeline}
        requestId={currentStockRequest?.id}
      />
    </div>
  );
};

export default StockRequestDetailsPage;
